import React, { useState } from 'react';

const API = process.env.REACT_APP_API_URL || 'http://localhost:3001';

// ─────────────────────────────────────────────────────────────────────────────
// WHO SIGNS FOR THE FIRM — AND HOW AN EXISTING PARTNER BECOMES THAT PERSON.
//
// 🚨 THERE WAS NO WAY TO DO THIS FROM ANY SCREEN. account_role = 'broker' could
// only ever be set by registering through a staff invite that carried
// grants_broker_role, and that write is guarded on `account_role IS NULL`. A
// partner who was already registered could not be promoted by anybody.
//
// Travis Pryor registered on 7 Apr 2026, before individual and firm accounts were
// separated. He is the sole member of "Travis Pryor, Broker", he IS the broker of
// record, and his own dashboard told him his fees were held waiting for his
// firm's broker to counter-sign his agreement. There was nobody else to ask and
// no control to fix it with.
//
// ⚠️ IT ASKS FOR A REASON, like the archive flow, because the role confers
// firm-wide sight of every buyer the firm has referred, the authority to
// counter-sign its members' agreements, and control of where its money goes. An
// authority grant with nothing recording why is the shape nobody can review.
// ─────────────────────────────────────────────────────────────────────────────

export default function BrokerOfRecordField({ partner, token, onGranted }) {
  const [open, setOpen] = useState(false);
  const [reason, setReason] = useState('');
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState(null);
  const [needsReplace, setNeedsReplace] = useState(false);

  // 🔑 A HOMEBUILDER HAS NO BROKER OF RECORD, and neither does a partner with no
  // firm — the role is about an account.
  if (!partner || partner.type === 'homebuilder' || !partner.account_id) return null;

  const isBroker = partner.account_role === 'broker';

  const grant = async (replace) => {
    setBusy(true); setMsg(null);
    try {
      const res = await fetch(`${API}/api/admin/partners/${partner.id}/broker-of-record`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ reason, replace: !!replace }),
      });
      const d = await res.json();
      if (!res.ok) {
        // 🔑 A HANDOVER IS A SECOND CONFIRMATION, not a failure. The server names
        // the current broker and refuses until somebody says they mean it.
        if (d.code === 'BROKER_EXISTS') setNeedsReplace(true);
        setMsg(d.error || 'That could not be recorded.');
        return;
      }
      // ⚠️ WHAT IT ACTUALLY CHANGED. The reason a rep is here is the
      // counter-signature, so saying "saved" and leaving them to re-read the
      // task list is the one unhelpful answer.
      setMsg(d.countersignature_still_outstanding
        ? '✓ Recorded — but a counter-signature is still outstanding. Check the task list.'
        : '✓ Recorded. The counter-signature requirement is gone.');
      setOpen(false); setReason(''); setNeedsReplace(false);
      if (onGranted) onGranted();
    } catch (_) {
      setMsg('Could not reach the server. Nothing was recorded.');
    } finally { setBusy(false); }
  };

  return (
    <div style={{ marginTop: 14, paddingTop: 12, borderTop: '1px solid #f3f4f6' }}>
      <div style={{ fontSize: 12, fontWeight: 600, color: '#374151', marginBottom: 3 }}>
        Broker of record
      </div>
      <div style={{ fontSize: 13, color: isBroker ? '#166534' : '#6b7280' }}>
        {isBroker
          ? `This partner signs for ${partner.company || 'their firm'}.`
          : 'Not this partner.'}
      </div>

      {!isBroker && !open && (
        <>
          {/* ⚠️ THE CONSEQUENCE IS STATED BEFORE THE BUTTON, not after it. */}
          <div style={{ fontSize: 11.5, color: '#9ca3af', marginTop: 4, lineHeight: 1.5 }}>
            Making them the broker of record removes the counter-signature their fees are held
            for, and gives them sight of every buyer their firm has referred plus control of its
            payout details.
          </div>
          <button type="button" onClick={() => setOpen(true)}
            style={{ marginTop: 7, fontSize: 12, fontWeight: 700, color: '#3a6ad9',
              background: '#fff', border: '1px solid #c5d3f5', borderRadius: 7,
              padding: '5px 11px', cursor: 'pointer' }}>
            Make them the broker of record
          </button>
        </>
      )}

      {open && (
        <div style={{ marginTop: 8, display: 'grid', gap: 7 }}>
          <textarea value={reason} rows={2}
            placeholder="Why are they the firm's broker of record? Required."
            onChange={(e) => setReason(e.target.value)}
            style={{ width: '100%', padding: '7px 9px', fontSize: 12.5, borderRadius: 7,
              border: '1px solid #d1d5db', boxSizing: 'border-box', resize: 'vertical',
              fontFamily: 'inherit' }} />
          <div style={{ display: 'flex', gap: 8 }}>
            <button type="button" disabled={busy || !reason.trim()}
              onClick={() => grant(needsReplace)}
              style={{ fontSize: 12, fontWeight: 700, color: '#fff',
                background: reason.trim() ? '#3a6ad9' : '#c7d2e8', border: 0, borderRadius: 7,
                padding: '6px 13px', cursor: reason.trim() ? 'pointer' : 'not-allowed' }}>
              {busy ? 'Recording…' : needsReplace ? 'Hand it over anyway' : 'Record it'}
            </button>
            <button type="button"
              onClick={() => { setOpen(false); setReason(''); setMsg(null); setNeedsReplace(false); }}
              style={{ fontSize: 12, color: '#6b7280', background: 'transparent', border: 0,
                cursor: 'pointer' }}>
              Cancel
            </button>
          </div>
        </div>
      )}

      {msg && (
        <div style={{ fontSize: 12, marginTop: 6, lineHeight: 1.5,
          color: msg.startsWith('✓') ? '#166534' : '#b91c1c' }}>{msg}</div>
      )}
    </div>
  );
}
